import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, AlertTriangle, CheckCircle2, RefreshCw, Radio, Gavel, Wrench } from "lucide-react";
import api from "@/lib/api";
import { AdminShell } from "@/components/AdminSidebar";
import { toast } from "sonner";

const SEV = {
  L0: { label: "OK", cls: "text-race-volt border-race-volt/40" },
  L1: { label: "L1 · Local", cls: "text-amber-400 border-amber-400/40" },
  L2: { label: "L2 · Cross-sector", cls: "text-orange-400 border-orange-400/40" },
  L3: { label: "L3 · High impact", cls: "text-race-red border-race-red/50" },
  L4: { label: "L4 · Critical", cls: "text-race-red border-race-red" },
};

export default function RaceControl() {
  const [data, setData] = useState(null);
  const [rl, setRl] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/race-control").then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
    api.get("/admin/race-logic").then((r) => setRl(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // live refresh every 30s
    return () => clearInterval(t);
  }, [load]);

  const events = data?.events || [];
  const alerts = events.filter((e) => !e.ok);
  const ready = events.filter((e) => e.ok);

  const reverify = (cid) => {
    api.post(`/admin/race-logic/enforce/${cid}`).then((r) => {
      toast.success(`Race Logic: ${r.data.status}${Object.keys(r.data.fixes_applied || {}).length ? " · auto-fixed" : ""}`);
      load();
    }).catch(() => toast.error("Re-verify failed"));
  };
  const rlCounts = rl?.counts || {};
  const rlItems = rl?.contests || [];

  return (
    <AdminShell>
      <div data-testid="race-control-page">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
        <div className="flex items-center gap-2">
          <Radio size={20} className="text-race-red" />
          <h1 className="font-heading text-3xl">Race Control</h1>
        </div>
        <button onClick={load} data-testid="rc-refresh" className="flex items-center gap-1.5 text-sm border border-race-line hover:border-race-txt rounded-sm px-3 py-1.5 transition-colors">
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>
      <p className="text-race-sub text-sm mb-6">Prime + specialized-agent readiness for upcoming contests. Auto-refreshes every 30s.</p>

      <div className="flex flex-wrap gap-2 mb-6">
        {(data?.sectors || []).map((s) => (
          <span key={s} data-testid={`rc-sector-${s}`} className="text-[11px] font-mono uppercase tracking-wide border border-race-line rounded-full px-3 py-1 text-race-sub">{s.replace("_", " ")}</span>
        ))}
      </div>

      {/* RACE LOGIC compliance - the Golden Standard, checks-and-balances */}
      {rl && (
        <div className="border border-race-line bg-race-surface rounded-sm p-4 mb-8" data-testid="race-logic-panel">
          <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <Gavel size={16} className="text-race-red" />
              <h2 className="font-heading text-xl">Race Logic Compliance</h2>
              <span className="text-[10px] font-mono text-race-sub">canon v{rl.version}</span>
            </div>
            <div className="flex items-center gap-2 text-xs font-mono">
              <span className="text-race-volt" data-testid="rl-count-compliant">{rlCounts.compliant || 0} compliant</span>
              <span className="text-amber-400" data-testid="rl-count-fixed">{rlCounts.fixed || 0} auto-fixed</span>
              <span className={`${(rlCounts.blocked || 0) > 0 ? "text-race-red" : "text-race-sub"}`} data-testid="rl-count-blocked">{rlCounts.blocked || 0} blocked</span>
            </div>
          </div>
          {(rlCounts.blocked || 0) > 0 && (
            <p className="text-race-red text-xs mb-3 flex items-center gap-1.5" data-testid="rl-block-alert">
              <AlertTriangle size={13} /> Blocked races are on HOLD and cannot accept registrants or start until fixed.
            </p>
          )}
          <div className="space-y-2">
            {rlItems.map((c) => {
              const color = c.status === "blocked" ? "border-race-red/50" : c.status === "fixed" ? "border-amber-400/40" : "border-race-line";
              const Icon = c.status === "blocked" ? AlertTriangle : c.status === "fixed" ? Wrench : CheckCircle2;
              const icls = c.status === "blocked" ? "text-race-red" : c.status === "fixed" ? "text-amber-400" : "text-race-volt";
              return (
                <div key={c.contest_id} data-testid={`rl-item-${c.contest_id}`} className={`border ${color} rounded-sm px-3 py-2`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon size={14} className={`${icls} shrink-0`} />
                      <span className="font-medium truncate">{c.name}</span>
                      <span className={`text-[9px] uppercase font-mono rounded-full px-1.5 py-0.5 border ${color} ${icls}`} data-testid={`rl-status-${c.contest_id}`}>{c.status}</span>
                    </div>
                    <button onClick={() => reverify(c.contest_id)} data-testid={`rl-reverify-${c.contest_id}`} className="text-[11px] flex items-center gap-1 border border-race-line rounded-sm px-2 py-1 text-race-sub hover:text-race-txt hover:border-race-txt">
                      <RefreshCw size={11} /> Re-verify & fix
                    </button>
                  </div>
                  {c.blockers?.length > 0 && (
                    <ul className="mt-1.5 ml-6 list-disc text-[11px] text-race-red" data-testid={`rl-blockers-${c.contest_id}`}>
                      {c.blockers.map((b, i) => <li key={i}>{b}</li>)}
                    </ul>
                  )}
                  {c.status === "fixed" && Object.keys(c.fixes_applied || {}).length > 0 && (
                    <div className="mt-1 ml-6 text-[11px] text-amber-400">Auto-fixed: {Object.keys(c.fixes_applied).join(", ")}</div>
                  )}
                </div>
              );
            })}
            {rlItems.length === 0 && <p className="text-race-sub text-sm">No upcoming contests to verify.</p>}
          </div>
        </div>
      )}

      {/* ALERTS */}
      <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
        <AlertTriangle size={16} className="text-race-red" />
        Needs attention <span data-testid="rc-alert-count" className="text-race-red">({data?.alert_count ?? 0})</span>
      </h2>
      {alerts.length === 0 ? (
        <div className="border border-race-line rounded-md p-6 text-center text-race-sub text-sm mb-8" data-testid="rc-no-alerts">
          <ShieldCheck size={22} className="text-race-volt mx-auto mb-2" /> All upcoming contests are race-ready.
        </div>
      ) : (
        <div className="space-y-3 mb-8">
          {alerts.map((e) => <Card key={e.contest_id} e={e} />)}
        </div>
      )}

      {/* READY */}
      <h2 className="text-base font-semibold flex items-center gap-2 mb-3">
        <CheckCircle2 size={16} className="text-race-volt" /> Race-ready ({ready.length})
      </h2>
      {ready.length === 0
        ? <p className="text-race-sub text-sm">No upcoming contests in the readiness window.</p>
        : <div className="space-y-3">{ready.map((e) => <Card key={e.contest_id} e={e} />)}</div>}
      </div>
    </AdminShell>
  );
}

function Card({ e }) {
  const sev = SEV[e.severity] || SEV.L1;
  return (
    <div data-testid={`rc-card-${e.contest_id}`} className={`bg-race-surface border rounded-md px-4 py-3 ${e.ok ? "border-race-line" : "border-race-red/40"}`}>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to={`/contests/${e.contest_id}`} className="font-medium truncate hover:text-race-red transition-colors">{e.contest_name || "Contest"}</Link>
        <div className="flex items-center gap-2 shrink-0">
          {e.starts_in_minutes != null && <span className="text-xs text-race-sub tabular">in {e.starts_in_minutes} min</span>}
          <span className={`text-[11px] font-mono uppercase tracking-wide border rounded-full px-2.5 py-0.5 ${sev.cls}`}>{sev.label}</span>
        </div>
      </div>
      {e.issues?.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid={`rc-issues-${e.contest_id}`}>
          {e.issues.map((i, idx) => (
            <li key={idx} className="text-xs text-race-sub flex items-start gap-2">
              <span className="text-race-red font-mono uppercase text-[10px] mt-0.5">{i.sector}</span>
              <span>{i.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
